---
title: Refunds Policy
---

<script lang="ts">
	import LegalContact from '$lib/components/LegalContact.svelte';
	import RefundPolicyIntro from '$lib/components/RefundPolicyIntro.svelte';

	export let data;
</script>

# Refunds Policy

<RefundPolicyIntro config={data.config} />

## Digital Products

All digital products sold through our website are delivered electronically. Due to the instant delivery nature of digital products, refunds are generally not available once access has been provided. However, we may provide refunds in the following circumstances:

### Digital Product Exceptions

We may provide a refund or replacement at our sole discretion in the following limited circumstances:

- **Duplicate Charges**: If you are charged more than once for the same product due to a technical error
- **Failed Delivery**: If you did not receive access to your digital product after purchase (for example, failed delivery of a download link or access credentials)
- **Product Defect**: If the digital product is materially different from what was described or advertised
- **Unauthorized Purchase**: If the purchase was made without your authorization (subject to verification)

In these cases, please contact us within **7 days of purchase** at [contact@artificerinnovations.com](mailto:contact@artificerinnovations.com) and include your order details. We will work with you to ensure you receive access to the product you purchased or, if necessary, issue a refund.

### Customer Satisfaction

We stand behind the quality of our digital products. If you have concerns about the content or quality, please contact us first so we can address your concerns.

### Pre-Order Products

If you purchase a digital product on pre-order and it is significantly delayed beyond the advertised release date, you may request a refund by contacting us at [contact@artificerinnovations.com](mailto:contact@artificerinnovations.com).

## Consulting Services

### Service Engagement Cancellation

For consulting services, cancellation and refund terms are specified in individual service agreements. Generally:

- **Before Service Commencement**: Full refund available if cancelled before work begins
- **During Service Delivery**: Refunds are prorated based on work completed and are subject to the terms of your specific service agreement
- **After Service Completion**: No refunds are available for completed services

### Service Agreement Terms

Each consulting engagement includes specific terms regarding:
- Cancellation policies
- Payment schedules
- Deliverable requirements
- Refund conditions

Please refer to your individual service agreement for specific refund terms applicable to your engagement.

## Subscription Services

### Cancellation Policy

If we offer subscription-based services in the future, the following cancellation terms will apply:

#### Monthly Subscriptions
- **Cancellation**: You may cancel your subscription at any time through your account settings or by contacting us
- **Effective Date**: Cancellation takes effect at the end of your current billing period
- **Access**: You will retain access to subscription benefits until the end of your paid period
- **Refunds**: No refunds for unused time in the current billing period

#### Annual Subscriptions
- **Cancellation**: You may cancel your annual subscription at any time
- **Refund Window**: Full refund available if cancelled within 14 days of purchase
- **After 14 Days**: No refunds available, but cancellation prevents future billing
- **Access**: You retain access until the end of your paid annual period

#### Recurring Billing
- **Automatic Renewal**: Subscriptions automatically renew unless cancelled before the renewal date
- **Payment Processing**: We will charge your payment method on the renewal date
- **Failed Payments**: We will notify you if payment fails and may suspend service after multiple failed attempts
- **Receipts**: You will receive a receipt after successful payment
- **Payment Method**: Ensure your payment method is current to avoid service interruption

### Subscription Cancellation Process

To cancel a subscription:

1. **Online**: Log into your account and navigate to subscription settings
2. **Email**: Contact us at [contact@artificerinnovations.com](mailto:contact@artificerinnovations.com) with your subscription details
3. **Confirmation**: You will receive email confirmation of your cancellation
4. **Effective Date**: Cancellation takes effect at the end of your current billing period

### Subscription Modifications

- **Plan Changes**: You may upgrade or downgrade your subscription plan at any time
- **Proration**: Plan changes are prorated based on your billing cycle
- **Effective Date**: Changes take effect immediately or at the next billing cycle, depending on the modification

## Refund Processing

### Timeline

- **Digital Products**: Refunds will be processed within 5-10 business days of approval
- **Consulting Services**: Refunds will be processed according to the terms specified in your service agreement

### Method

Refunds will be issued to the original payment method used for the purchase. Processing times may vary depending on your financial institution.

### Notification

You will receive email confirmation when your refund has been processed.

## Non-Refundable Items

The following are not eligible for refunds:

- **Completed Consulting Services**: Services that have been fully delivered according to the service agreement
- **Downloaded Digital Products**: Digital products that have been successfully downloaded or accessed
- **Custom Work**: Any custom consulting work or deliverables that have been completed
- **Third-Party Services**: Any third-party services or products recommended or facilitated by us
- **Subscription Services**: Used portions of subscription periods (except as specified in subscription terms)
- **Promotional or Discounted Items**: Items purchased with special pricing or promotional codes (unless otherwise specified)

## Dispute Resolution

If you believe you are entitled to a refund that has been denied, you may:

1. **Contact Us**: Reach out to us at [contact@artificerinnovations.com](mailto:contact@artificerinnovations.com) with detailed information about your concern
2. **Provide Documentation**: Include relevant documentation such as order confirmations, service agreements, or correspondence
3. **Review Process**: We will review your request and respond within 5 business days

## Chargebacks and Payment Disputes

We encourage customers to contact us directly to resolve any issues before initiating chargebacks or payment disputes. Chargebacks and disputes may result in additional fees and may affect your ability to purchase our services or products in the future.

## Changes to This Policy

We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting on our website. Your continued use of our services or purchase of our products after changes are posted constitutes acceptance of the updated policy.

## Contact Us

If you have any questions about this Refund Policy or need to request a refund, please contact us at:

<LegalContact config={data.config} />

When contacting us about a refund, please include:
- Your order number or service agreement reference
- Date of purchase or service engagement
- Description of the issue
- Any relevant documentation

---

*This Refund Policy is effective as of September 7, 2025, and applies to all purchases and service engagements made after this date.*
